import {
  Combobox,
  Input,
  Option,
  mergeClasses,
} from "@fluentui/react-components";
import {
  AddRegular,
  ChevronDownRegular,
  ChevronUpRegular,
  DeleteRegular,
} from "@fluentui/react-icons";
import { ActionDialog } from "components/actionDialog/ActionDialog";
import { useMemo, useRef, useState } from "react";
import { DEFAULT_TEAM_ROLES } from "apps/project-iq/constants/defaultPeopleData";
import { useTeamRoleCardStyles } from "./TeamRoleCard.styles";
import { ITeamRoleCardProps } from "./TeamRoleCard.types";
import { UNASSIGNED_LABEL } from "./WorkspacePeople.constants";

export const TeamRoleCard = ({
  role,
  members,
  canEdit,
  onRoleChange,
  onDelete,
}: ITeamRoleCardProps): JSX.Element => {
  const styles = useTeamRoleCardStyles();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDeleteRoleDialogOpen, setIsDeleteRoleDialogOpen] = useState(false);
  const [deletePendingIndex, setDeletePendingIndex] = useState<number | null>(
    null,
  );
  const [isEditingAssignment, setIsEditingAssignment] = useState(false);
  const [assignmentDraft, setAssignmentDraft] = useState(role.assignedMember);
  const [editingResponsibilityIndex, setEditingResponsibilityIndex] = useState<
    number | null
  >(null);
  const [responsibilityDraft, setResponsibilityDraft] = useState("");
  const newResponsibilityRef = useRef<HTMLInputElement>(null);
  const shouldSkipCommitRef = useRef(false);

  const handleToggleExpand = () => {
    setIsExpanded((prev) => !prev);
  };

  const handleAssignmentClick = () => {
    setAssignmentDraft(role.assignedMember);
    setIsEditingAssignment(true);
  };

  const handleAssignmentCommit = () => {
    const trimmed = assignmentDraft.trim() || UNASSIGNED_LABEL;
    onRoleChange({ ...role, assignedMember: trimmed });
    setIsEditingAssignment(false);
  };

  const handleAssignmentKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      shouldSkipCommitRef.current = true;
      setIsEditingAssignment(false);
    }
  };

  const handleResponsibilityClick = (index: number) => {
    setEditingResponsibilityIndex(index);
    setResponsibilityDraft(role.responsibilities[index]);
  };

  const handleResponsibilityCommit = (index: number) => {
    const trimmed = responsibilityDraft.trim();
    if (!trimmed) {
      handleDeleteResponsibility(index);
    } else {
      const next = role.responsibilities.map((r, i) =>
        i === index ? trimmed : r,
      );
      onRoleChange({ ...role, responsibilities: next });
    }
    setEditingResponsibilityIndex(null);
    setResponsibilityDraft("");
  };

  const handleResponsibilityKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (e.key === "Enter") handleResponsibilityCommit(index);
    if (e.key === "Escape") {
      setEditingResponsibilityIndex(null);
      setResponsibilityDraft("");
    }
  };

  const handleDeleteResponsibility = (index: number) => {
    const next = role.responsibilities.filter((_, i) => i !== index);
    onRoleChange({ ...role, responsibilities: next });
  };

  const handleAddResponsibility = () => {
    const next = [...role.responsibilities, ""];
    onRoleChange({ ...role, responsibilities: next });
    setEditingResponsibilityIndex(next.length - 1);
    setResponsibilityDraft("");
    setTimeout(() => newResponsibilityRef.current?.focus(), 50);
  };

  const isUnassigned =
    !role.assignedMember || role.assignedMember === UNASSIGNED_LABEL;

  const defaultResponsibilitySet = useMemo(() => {
    const match = DEFAULT_TEAM_ROLES.find((r) => r.id === role.id);
    return new Set(match?.responsibilities ?? []);
  }, [role.id]);

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h3 className={styles.roleName}>{role.name}</h3>
          <div className={styles.assignedMemberWrapper}>
            {isEditingAssignment ? (
              <Combobox
                size="small"
                freeform
                defaultOpen
                className={styles.assignedValueInput}
                value={assignmentDraft}
                placeholder={UNASSIGNED_LABEL}
                onChange={(e) => setAssignmentDraft(e.target.value)}
                onOptionSelect={(_, data) => {
                  shouldSkipCommitRef.current = true;
                  const value = data.optionText ?? "";
                  onRoleChange({
                    ...role,
                    assignedMember: value || UNASSIGNED_LABEL,
                  });
                  setIsEditingAssignment(false);
                }}
                onBlur={() => {
                  setTimeout(() => {
                    if (!shouldSkipCommitRef.current) {
                      handleAssignmentCommit();
                    }
                    shouldSkipCommitRef.current = false;
                  }, 0);
                }}
                onKeyDown={handleAssignmentKeyDown}
                autoFocus
                aria-label={`Assigned member for ${role.name}`}
              >
                {[...members]
                  .sort((a, b) => a.displayName.localeCompare(b.displayName))
                  .map((person) => (
                    <Option key={person.userId} value={person.displayName}>
                      {person.displayName}
                    </Option>
                  ))}
              </Combobox>
            ) : canEdit ? (
              <span
                className={mergeClasses(
                  styles.assignedValue,
                  isUnassigned ? styles.assignedUnassigned : undefined,
                )}
                onClick={handleAssignmentClick}
                role="button"
                tabIndex={0}
                aria-label={`Assigned to ${role.assignedMember}. Click to edit.`}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ")
                    handleAssignmentClick();
                }}
              >
                {role.assignedMember || UNASSIGNED_LABEL}
              </span>
            ) : (
              <span
                className={mergeClasses(
                  styles.assignedValue,
                  isUnassigned ? styles.assignedUnassigned : undefined,
                )}
              >
                {role.assignedMember || UNASSIGNED_LABEL}
              </span>
            )}
          </div>
        </div>
        <div className={styles.headerActions}>
          {!role.isDefault && canEdit && (
            <button
              className={styles.deleteButton}
              onClick={() => setIsDeleteRoleDialogOpen(true)}
              aria-label={`Delete role: ${role.name}`}
            >
              <DeleteRegular aria-hidden="true" />
            </button>
          )}
          <button
            className={styles.chevronButton}
            onClick={handleToggleExpand}
            aria-label={
              isExpanded
                ? `Collapse responsibilities for ${role.name}`
                : `Expand responsibilities for ${role.name}`
            }
          >
            {isExpanded ? (
              <ChevronUpRegular aria-hidden="true" />
            ) : (
              <ChevronDownRegular aria-hidden="true" />
            )}
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className={styles.responsibilitiesSection}>
          <ul
            className={styles.responsibilityList}
            aria-label="Responsibilities"
          >
            {role.responsibilities.map((responsibility, index) => (
              <li key={index} className={styles.responsibilityItem}>
                <span
                  className={styles.responsibilityBullet}
                  aria-hidden="true"
                />
                {editingResponsibilityIndex === index ? (
                  <Input
                    ref={
                      index === role.responsibilities.length - 1
                        ? newResponsibilityRef
                        : undefined
                    }
                    size="small"
                    className={styles.responsibilityItemInput}
                    value={responsibilityDraft}
                    onChange={(_e, data) => setResponsibilityDraft(data.value)}
                    onBlur={() => handleResponsibilityCommit(index)}
                    onKeyDown={(e) => handleResponsibilityKeyDown(e, index)}
                    placeholder="Responsibility description"
                    autoFocus
                    aria-label={`Edit responsibility ${index + 1} for ${role.name}`}
                  />
                ) : canEdit && !defaultResponsibilitySet.has(responsibility) ? (
                  <>
                    <span
                      className={styles.responsibilityText}
                      onClick={() => handleResponsibilityClick(index)}
                      role="button"
                      tabIndex={0}
                      aria-label={`Edit: ${responsibility}`}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ")
                          handleResponsibilityClick(index);
                      }}
                    >
                      {responsibility}
                    </span>
                    <button
                      className={styles.deleteButton}
                      onClick={() => setDeletePendingIndex(index)}
                      aria-label={`Delete responsibility: ${responsibility}`}
                    >
                      <DeleteRegular aria-hidden="true" />
                    </button>
                  </>
                ) : (
                  <span className={styles.responsibilityText}>
                    {responsibility}
                  </span>
                )}
              </li>
            ))}
          </ul>
          {canEdit && (
            <button
              className={styles.addResponsibilityButton}
              onClick={handleAddResponsibility}
              aria-label={`Add responsibility to ${role.name}`}
            >
              <AddRegular aria-hidden="true" />
              Add responsibility
            </button>
          )}
        </div>
      )}

      <ActionDialog
        isVisible={deletePendingIndex !== null}
        title="Delete responsibility?"
        message={
          deletePendingIndex !== null &&
          role.responsibilities[deletePendingIndex]
            ? `Are you sure you want to delete "${role.responsibilities[deletePendingIndex]}"? This cannot be undone.`
            : "Are you sure you want to delete this responsibility? This cannot be undone."
        }
        confirmationMessage="Delete"
        dismissMessage="Cancel"
        onConfirmation={() => {
          if (deletePendingIndex !== null) {
            handleDeleteResponsibility(deletePendingIndex);
            setDeletePendingIndex(null);
          }
        }}
        onDismiss={() => setDeletePendingIndex(null)}
      />

      <ActionDialog
        isVisible={isDeleteRoleDialogOpen}
        title="Delete role?"
        message={`Are you sure you want to delete the "${role.name}" role? This cannot be undone.`}
        confirmationMessage="Delete"
        dismissMessage="Cancel"
        onConfirmation={() => {
          setIsDeleteRoleDialogOpen(false);
          onDelete(role.id);
        }}
        onDismiss={() => setIsDeleteRoleDialogOpen(false)}
      />
    </div>
  );
};
